import { useState } from 'react';
import { useQuery } from '@tanstack/react-query';
import { Link, useNavigate } from '@tanstack/react-router';
import { Badge, Button, EmptyState, Section, Spinner, type Tone } from '@zt/ui';
import type { ai } from '@zt/core';
import { api, newIdempotencyKey, type Jsonify } from '../../lib/api';
import { HelpTip, PageFrame } from '../../lib/help';
import { useI18n, useT } from '../../lib/i18n';
import { useApiMutation } from '../../lib/mutation';
import { usePermissions } from '../../lib/session';
import { StorePicker, useStoreChoice } from '../payments';
import { AiButton } from './shared';

/**
 * SEO önerileri (F9-04): otomatik kümeler, konu haritası, uzun kuyruk, içerik boşlukları, Google ürün başlıkları.
 * Hepsi yapay zekâsız; yalnızca Google başlığında isteğe bağlı yapay zekâ önerisi.
 */

type R<F extends (...a: never[]) => unknown> = Jsonify<Awaited<ReturnType<F>>>;
type Suggestions = R<typeof ai.seoSuggestions>;
type TitleRow = R<typeof ai.merchantTitleReport>[number];
type AiTitle = R<typeof ai.aiMerchantTitle>;

const LINK_TONE: Record<string, Tone> = { yes: 'moss', no: 'amber', unknown: 'neutral' };

export function SeoSuggestionsPage() {
  const t = useT();
  const { can } = usePermissions();
  const { stores, loading, storeId, setStoreId } = useStoreChoice();
  const q = useQuery({
    queryKey: ['seo-suggestions', storeId],
    queryFn: () => api.get<Suggestions>(`/v1/admin/seo/suggestions?storeId=${storeId}`),
    enabled: !!storeId && can('seo.read'),
  });
  if (loading) return <Spinner label={t('common.loading')} />;
  const d = q.data;
  return (
    <PageFrame helpKey="page.ai_seo">
      <p className="max-w-[70ch] text-sm text-ink-2">{t('ai.seo.lead')}</p>
      {!stores.length ? (
        <EmptyState title={t('admin.payments.noStore')} description={t('admin.payments.noStoreLead')} />
      ) : (
        <>
          <StorePicker stores={stores} storeId={storeId} onChange={setStoreId} />
          {can('seo.read') &&
            (!d ? (
              <Spinner label={t('common.loading')} />
            ) : (
              <>
                {!d.synced && <p className="text-sm text-amber">{t('ai.seo.notSynced')}</p>}
                <ClustersSection storeId={storeId} d={d} />
                <TopicsSection d={d} />
                <LongTailSection storeId={storeId} d={d} />
                <GapsSection storeId={storeId} d={d} />
              </>
            ))}
          {can('catalog.product.read') && <MerchantTitlesSection />}
        </>
      )}
    </PageFrame>
  );
}

function ClustersSection({ storeId, d }: { storeId: string; d: Suggestions }) {
  const t = useT();
  const { can } = usePermissions();
  const [picked, setPicked] = useState<Set<string>>(new Set());
  const apply = useApiMutation(
    () =>
      api.post<{ updated: number }>('/v1/admin/seo/suggestions/clusters', {
        storeId,
        clusters: d.proposals
          .filter((p) => picked.has(p.name))
          .map((p) => ({ name: p.name, keywordIds: p.keywords.map((k) => k.id) })),
      }),
    {
      invalidate: [['seo-suggestions', storeId], ['keywords']],
      success: (r) => t('ai.seo.applied', { count: r.updated }),
      onSuccess: () => setPicked(new Set()),
    },
  );
  const open = d.proposals.filter((p) => p.changes > 0);
  return (
    <Section title={t('ai.seo.clustersTitle')} action={<HelpTip k="seo_ai.clusters" />}>
      {!open.length ? (
        <p className="text-sm text-ink-2">{d.keywords ? t('ai.seo.noProposals') : t('ai.seo.emptyPool')}</p>
      ) : (
        <>
          <ul className="space-y-2">
            {open.map((p) => (
              <li key={p.name} className="rounded-md border border-line p-3 text-sm">
                <label className="flex items-start gap-2">
                  {can('seo.write') && (
                    <input
                      type="checkbox"
                      className="mt-1"
                      checked={picked.has(p.name)}
                      onChange={(e) => {
                        const n = new Set(picked);
                        if (e.target.checked) n.add(p.name);
                        else n.delete(p.name);
                        setPicked(n);
                      }}
                    />
                  )}
                  <span>
                    <span className="font-medium">{p.name}</span>{' '}
                    <span className="text-ink-3">
                      {t('ai.seo.keywordCount', { count: p.keywords.length })}
                    </span>
                    <span className="mt-1 flex flex-wrap gap-1">
                      {p.keywords.map((k) => (
                        <Badge key={k.id} tone={k.currentCluster ? 'slate' : 'neutral'}>
                          {k.keyword}
                          {k.currentCluster ? ` (${k.currentCluster})` : ''}
                        </Badge>
                      ))}
                    </span>
                  </span>
                </label>
              </li>
            ))}
          </ul>
          {can('seo.write') && (
            <Button
              className="mt-3"
              variant="primary"
              disabled={!picked.size}
              loading={apply.isPending}
              onClick={() => apply.mutate(undefined)}
            >
              {t('ai.seo.applyClusters', { count: picked.size })}
            </Button>
          )}
        </>
      )}
    </Section>
  );
}

function TopicsSection({ d }: { d: Suggestions }) {
  const t = useT();
  return (
    <Section title={t('ai.seo.topicsTitle')} action={<HelpTip k="seo_ai.topics" />}>
      {!d.topics.length ? (
        <p className="text-sm text-ink-2">{t('ai.seo.noTopics')}</p>
      ) : (
        <ul className="space-y-3">
          {d.topics.map((x) => (
            <li key={x.cluster} className="rounded-md border border-line p-3 text-sm">
              <p className="font-medium">
                {x.cluster} ·{' '}
                {t('ai.seo.pillar', { keyword: x.pillar.keyword, path: x.pillar.path ?? t('ai.seo.noPage') })}
              </p>
              {x.spokes.length > 0 && (
                <ul className="mt-1 space-y-0.5">
                  {x.spokes.map((s) => {
                    const st = s.linksToPillar === null ? 'unknown' : s.linksToPillar ? 'yes' : 'no';
                    return (
                      <li key={s.keyword} className="flex flex-wrap items-center gap-2">
                        <span>{s.keyword}</span>
                        <code className="text-ink-3">{s.path ?? t('ai.seo.noPage')}</code>
                        {s.path && <Badge tone={LINK_TONE[st]}>{t(`ai.seo.link.${st}`)}</Badge>}
                      </li>
                    );
                  })}
                </ul>
              )}
              {x.missingContent > 0 && (
                <p className="mt-1 text-amber">{t('ai.seo.missingContent', { count: x.missingContent })}</p>
              )}
            </li>
          ))}
        </ul>
      )}
    </Section>
  );
}

function LongTailSection({ storeId, d }: { storeId: string; d: Suggestions }) {
  const t = useT();
  const { formatNumber } = useI18n();
  const { can } = usePermissions();
  const [picked, setPicked] = useState<Set<string>>(new Set());
  const add = useApiMutation(() => api.post('/v1/admin/keywords/import', { storeId, queries: [...picked] }), {
    invalidate: [['seo-suggestions', storeId], ['keywords']],
    success: t('ai.seo.added'),
    onSuccess: () => setPicked(new Set()),
  });
  return (
    <Section title={t('ai.seo.longTailTitle')} action={<HelpTip k="seo_ai.long_tail" />}>
      {!d.longTail.length ? (
        <p className="text-sm text-ink-2">{t('ai.seo.noLongTail')}</p>
      ) : (
        <>
          <div className="overflow-x-auto rounded-md border border-line">
            <table className="w-full min-w-[32rem] text-sm">
              <caption className="sr-only">{t('ai.seo.longTailTitle')}</caption>
              <thead className="bg-surface-2 text-ink-2">
                <tr>
                  {can('seo.write') && <th className="w-8 px-3 py-2" />}
                  <th className="px-3 py-2 text-start font-medium">{t('ai.seo.query')}</th>
                  <th className="px-3 py-2 text-end font-medium">{t('ai.studio.impressions')}</th>
                  <th className="px-3 py-2 text-end font-medium">{t('ai.studio.position')}</th>
                  <th className="px-3 py-2 text-start font-medium">{t('ai.studio.f.intent')}</th>
                </tr>
              </thead>
              <tbody className="divide-y divide-line">
                {d.longTail.map((x) => (
                  <tr key={x.query}>
                    {can('seo.write') && (
                      <td className="px-3 py-2">
                        <input
                          type="checkbox"
                          aria-label={t('ai.seo.pick', { query: x.query })}
                          checked={picked.has(x.query)}
                          onChange={(e) => {
                            const n = new Set(picked);
                            if (e.target.checked) n.add(x.query);
                            else n.delete(x.query);
                            setPicked(n);
                          }}
                        />
                      </td>
                    )}
                    <td className="px-3 py-2">{x.query}</td>
                    <td className="px-3 py-2 text-end tabular-nums">{formatNumber(x.impressions)}</td>
                    <td className="px-3 py-2 text-end tabular-nums">
                      {x.positionX10 === null ? '—' : formatNumber(x.positionX10 / 10, 1)}
                    </td>
                    <td className="px-3 py-2">{x.intent ? t(`ai.studio.intents.${x.intent}`) : '—'}</td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
          {can('seo.write') && (
            <Button
              className="mt-3"
              disabled={!picked.size}
              loading={add.isPending}
              onClick={() => add.mutate(undefined)}
            >
              {t('ai.seo.addToPool', { count: picked.size })}
            </Button>
          )}
        </>
      )}
    </Section>
  );
}

function GapsSection({ storeId, d }: { storeId: string; d: Suggestions }) {
  const t = useT();
  const navigate = useNavigate();
  const { can } = usePermissions();
  const create = useApiMutation(
    (g: Suggestions['contentGaps'][number]) =>
      api.post<{ id: string }>('/v1/admin/content-studio/projects', {
        storeId,
        topic: g.keyword,
        keywordId: g.keywordId,
      }),
    {
      invalidate: [
        ['studio', 'list'],
        ['seo-suggestions', storeId],
      ],
      onSuccess: (r) => void navigate({ to: '/yapay-zeka/icerik-studyosu/$id', params: { id: r.id } }),
    },
  );
  return (
    <Section title={t('ai.seo.gapsTitle')} action={<HelpTip k="seo_ai.gaps" />}>
      {!d.contentGaps.length ? (
        <p className="text-sm text-ink-2">{t('ai.seo.noGaps')}</p>
      ) : (
        <ul className="divide-y divide-line rounded-md border border-line text-sm">
          {d.contentGaps.map((g) => (
            <li key={g.keywordId} className="flex flex-wrap items-center gap-2 px-3 py-2">
              <span className="flex-1">
                {g.keyword}
                {g.intent && <span className="text-ink-3"> · {t(`ai.studio.intents.${g.intent}`)}</span>}
              </span>
              {g.projectId ? (
                <Link
                  to="/yapay-zeka/icerik-studyosu/$id"
                  params={{ id: g.projectId }}
                  className="text-pine underline underline-offset-2"
                >
                  {t('ai.seo.openProject')}
                </Link>
              ) : (
                can('content.write') && (
                  <Button
                    size="sm"
                    loading={create.isPending && create.variables?.keywordId === g.keywordId}
                    onClick={() => create.mutate(g)}
                  >
                    {t('ai.seo.createProject')}
                  </Button>
                )
              )}
            </li>
          ))}
        </ul>
      )}
    </Section>
  );
}

function MerchantTitlesSection() {
  const t = useT();
  const { can } = usePermissions();
  const q = useQuery({
    queryKey: ['merchant-titles'],
    queryFn: () => api.get<TitleRow[]>('/v1/admin/merchant/title-suggestions'),
  });
  const [onlyIssues, setOnlyIssues] = useState(true);
  const [aiTitles, setAiTitles] = useState<Record<string, AiTitle>>({});
  const save = useApiMutation(
    (v: { productId: string; title: string | null; version: number }) =>
      api.put(`/v1/admin/products/${v.productId}/merchant-title`, { title: v.title, version: v.version }),
    { invalidate: [['merchant-titles']], success: t('ai.seo.titleSaved') },
  );
  const gen = useApiMutation(
    (productId: string) =>
      api.post<AiTitle>(`/v1/admin/products/${productId}/merchant-title/ai`, undefined, {
        idempotencyKey: newIdempotencyKey(),
      }),
    {
      invalidate: [['ai', 'availability']],
      onSuccess: (r, productId) => setAiTitles((x) => ({ ...x, [productId]: r })),
    },
  );
  const rows = (q.data ?? []).filter((r) => !onlyIssues || r.issues.length > 0);
  const write = can('catalog.product.write');
  return (
    <Section title={t('ai.seo.titlesTitle')} action={<HelpTip k="seo_ai.merchant_titles" />}>
      <p className="mb-3 max-w-[70ch] text-sm text-ink-2">{t('ai.seo.titlesLead')}</p>
      <label className="mb-3 flex items-center gap-2 text-sm">
        <input type="checkbox" checked={onlyIssues} onChange={(e) => setOnlyIssues(e.target.checked)} />
        {t('ai.seo.onlyIssues')}
      </label>
      {!q.data ? (
        <Spinner label={t('common.loading')} />
      ) : !rows.length ? (
        <p className="text-sm text-ink-2">{t('ai.seo.noTitleIssues')}</p>
      ) : (
        <ul className="space-y-3">
          {rows.map((r) => (
            <li key={r.productId} className="rounded-md border border-line p-3 text-sm">
              <p className="font-medium">{r.productTitle}</p>
              <p className="text-ink-2">
                {t('ai.seo.currentTitle')}: {r.current}{' '}
                <span className="text-ink-3">({r.current.length})</span>
              </p>
              {r.issues.length > 0 && (
                <p className="mt-1 flex flex-wrap gap-1">
                  {r.issues.map((i) => (
                    <Badge key={i} tone="amber">
                      {t(`ai.seo.issues.${i}`)}
                    </Badge>
                  ))}
                </p>
              )}
              {r.suggestion && (
                <p className="mt-2 flex flex-wrap items-center gap-2">
                  <span>
                    {t('ai.seo.suggested')}: {r.suggestion}{' '}
                    <span className="text-ink-3">({r.suggestion.length})</span>
                  </span>
                  {write && (
                    <Button
                      size="sm"
                      loading={save.isPending && save.variables?.productId === r.productId}
                      onClick={() =>
                        save.mutate({ productId: r.productId, title: r.suggestion, version: r.version })
                      }
                    >
                      {t('ai.seo.applyTitle')}
                    </Button>
                  )}
                </p>
              )}
              {aiTitles[r.productId] && (
                <p className="mt-2 flex flex-wrap items-center gap-2">
                  <span>
                    {t('ai.seo.aiSuggested')}: {aiTitles[r.productId]!.title}{' '}
                    <span className="text-ink-3">({aiTitles[r.productId]!.title.length})</span>
                  </span>
                  {write && (
                    <Button
                      size="sm"
                      onClick={() =>
                        save.mutate({
                          productId: r.productId,
                          title: aiTitles[r.productId]!.title,
                          version: r.version,
                        })
                      }
                    >
                      {t('ai.seo.applyTitle')}
                    </Button>
                  )}
                </p>
              )}
              <div className="mt-2 flex flex-wrap items-center gap-2">
                {write && (
                  <AiButton
                    loading={gen.isPending && gen.variables === r.productId}
                    onClick={() => gen.mutate(r.productId)}
                  >
                    {t('ai.seo.aiTitle')}
                  </AiButton>
                )}
                {write && r.merchantTitle && (
                  <Button
                    size="sm"
                    variant="ghost"
                    onClick={() => save.mutate({ productId: r.productId, title: null, version: r.version })}
                  >
                    {t('ai.seo.resetTitle')}
                  </Button>
                )}
              </div>
            </li>
          ))}
        </ul>
      )}
    </Section>
  );
}
